import Image from 'next/image';
import { debounce } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import YouTube, { YouTubeProps } from 'react-youtube';

import {
  Box,
  Button,
  Flex,
  Heading,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useBreakpointValue,
  useDisclosure,
  VisuallyHidden,
} from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { SanityImage } from '~/components/sanity-image';
import { trackEvent } from '~/features/analytics/track-event';
import { SanityImageType } from '~/features/page-template/models/image';

import { useExperimentalSegmentEvents } from '../hooks';
import { CheckedIcon, PlayIcon } from '../icons';

import { SectionHeader } from './section-header';
import { TestimonialCarousel } from './testimonials-carousel';

import 'swiper/css';
import 'swiper/css/mousewheel';

export type UdacityOutcomesSectionProps = StaticUdacityOutcomesSectionProps;

export function UdacityOutcomesSection({
  titleKey,
  subtitleKey,
  descriptionKey,
  featuredStory,
  testimonials,
  outcomes,
  mentorship,
}: UdacityOutcomesSectionProps) {
  const { t } = useTranslation();
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';
  const handleInteraction = debounce(() => {
    if (isExperimentalTrackEventsEnabled) {
      trackEvent({
        name: 'Homepage CTA Clicked',
        type: 'click',
        label: 'success_stories_scroll_clicked',
      });
    }
  }, 100);

  return (
    <Flex
      as='section'
      flexDirection='column'
      gap={{
        base: '56px',
        md: '72px',
      }}
      padding={{
        base: '56px 0px',
        md: '120px 0px',
      }}
    >
      <ContentContainer margin='0 auto' maxWidth='766px' width='100%'>
        <SectionHeader
          color='grey.500'
          descriptionSlot={<Text color='grey.500'>{t(descriptionKey)}</Text>}
          iconSlot={<CheckedIcon color='grey.500' height='18px' width='18px' />}
          subtitle={t(subtitleKey) ?? ''}
          titleSlot={
            <Heading
              as='h2'
              fontWeight='600'
              lineHeight='1.1'
              fontSize={{
                base: '2.25rem',
                md: '3.375rem',
              }}
            >
              {t(titleKey)}
            </Heading>
          }
        />
      </ContentContainer>
      <FeaturedStory
        backgroundImageUrl={featuredStory.backgroundImageUrl}
        company={featuredStory.company}
        descriptionKey={featuredStory.descriptionKey}
        mobileBackgroundImageUrl={featuredStory.mobileBackgroundImageUrl}
        titleKey={featuredStory.titleKey}
        video={featuredStory.video}
      />
      <TestimonialCarousel testimonials={testimonials} onInteraction={handleInteraction} />
      <OutcomesSection outcomes={outcomes} />
      <MentorshipAside
        companiesMentorsWorkAt={mentorship.companiesMentorsWorkAt}
        descriptionKey={mentorship.descriptionKey}
        titleKey={mentorship.titleKey}
      />
    </Flex>
  );
}

type FeaturedStoryProps = StaticUdacityOutcomesSectionProps['featuredStory'];
function FeaturedStory({
  company,
  titleKey,
  descriptionKey,
  video,
  backgroundImageUrl,
  mobileBackgroundImageUrl,
}: FeaturedStoryProps) {
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const isExperimentalTrackEventsEnabled = useExperimentalSegmentEvents('dino')?.variant === 'on';
  const videoHeight = useBreakpointValue(
    {
      base: '240px',
      md: '649px',
    },
    { fallback: 'md' }
  );
  const videoOptions: YouTubeProps['opts'] = {
    height: videoHeight,
    width: '100%',
    host: 'https://www.youtube-nocookie.com',
    playerVars: {
      rel: 0,
      autoplay: 1,
    },
  };

  const handleWatchFeaturedStoryClick = () => {
    if (isExperimentalTrackEventsEnabled) {
      trackEvent({
        name: 'Homepage CTA Clicked',
        type: 'click',
        label: 'watch_featured_story_clicked',
      });
    }
    onOpen();
  };

  return (
    <ContentContainer>
      <Box
        as='article'
        bgColor='grey.900'
        borderRadius='12px'
        minHeight={{ base: '598px', md: '540px' }}
        overflow='hidden'
        position='relative'
      >
        <Box
          inset={0}
          position='absolute'
          display={{
            base: 'none',
            md: 'block',
          }}
        >
          <Image alt='' loading='lazy' sizes='1240px' src={backgroundImageUrl} style={{ objectFit: 'cover' }} fill />
        </Box>
        <Box
          inset={0}
          margin='0 auto'
          maxWidth='398px'
          position='absolute'
          _after={{
            content: '""',
            display: 'block',
            position: 'absolute',
            inset: 0,
            bgGradient:
              'linear(90deg,rgba(10, 11, 15, 1) 0%, rgba(10, 11, 15, 0) 15%, rgba(10, 11, 15, 0) 85%, rgba(10, 11, 15, 1) 100%);',
          }}
          display={{
            base: 'block',
            md: 'none',
          }}
        >
          <Image
            alt=''
            loading='lazy'
            sizes='716px'
            src={mobileBackgroundImageUrl}
            style={{ objectFit: 'cover', objectPosition: 'top' }}
            fill
          />
        </Box>
        <Box
          bottom={0}
          left={0}
          padding={4}
          position='absolute'
          right={0}
          top={{
            base: '240px',
            md: 0,
          }}
        >
          <Flex
            alignItems='flex-start'
            backdropBlur='100px'
            bgColor='hsla(0, 0%, 100%, 0.06)'
            border='0.5px solid'
            borderRadius='6px'
            flexDirection='column'
            gap={6}
            height='100%'
            justifyContent='space-between'
            width='100%'
            maxWidth={{
              md: '350px',
            }}
            padding={{
              base: '20px',
              md: 8,
            }}
            sx={{
              backdropFilter: 'blur(24px)',
              borderImageSource:
                'linear-gradient(226.27deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0.08) 75.55%)',
            }}
          >
            <Flex flexDirection='column' gap={4}>
              <Box>{!!company.logo && !!company.name && <SanityImage alt={company.name} image={company.logo} />}</Box>
              <Heading
                as='h3'
                color='white'
                fontWeight='600'
                fontSize={{
                  base: '1.75rem',
                  md: '2.25rem',
                }}
                lineHeight={{
                  base: '1.75rem',
                  md: '2.25rem',
                }}
              >
                {t(titleKey)}
              </Heading>
              <Text
                color='grey.300'
                noOfLines={{
                  base: 4,
                  md: Number.POSITIVE_INFINITY,
                }}
              >
                {t(descriptionKey)}
              </Text>
            </Flex>
            <Button
              colorScheme='white'
              leftIcon={<PlayIcon height='18px' width='18px' />}
              variant='outline'
              width='full'
              onClick={handleWatchFeaturedStoryClick}
            >
              {t('common.watchFeaturedStory')}
            </Button>
            <Modal isOpen={isOpen} size='6xl' isCentered onClose={onClose}>
              <ModalOverlay backdropFilter='blur(10px) brightness(0.25)' />
              <ModalContent bg='grey.900' color='white'>
                <VisuallyHidden as={ModalHeader}>{t(titleKey)}</VisuallyHidden>
                <ModalCloseButton color='white' />
                <ModalBody>
                  {video?.youTubeVideoId && (
                    <YouTube
                      opts={videoOptions}
                      title={video.title || 'Featured Story Video'}
                      videoId={video.youTubeVideoId}
                    />
                  )}
                </ModalBody>
              </ModalContent>
            </Modal>
          </Flex>
        </Box>
      </Box>
    </ContentContainer>
  );
}

type OutcomesSectionProps = {
  outcomes: StaticUdacityOutcomesSectionProps['outcomes'];
};
function OutcomesSection({ outcomes }: OutcomesSectionProps) {
  const { t } = useTranslation();
  const supSlot = (
    <Box
      alignItems='center'
      as='sup'
      border='1px solid'
      borderRadius='50%'
      color='grey.900'
      display='inline-flex'
      flexShrink={0}
      fontSize='0.4375rem'
      fontWeight='700'
      height='12px'
      justifyContent='center'
      lineHeight='1'
      width='12px'
    >
      1
    </Box>
  );
  return (
    <ContentContainer contentMaxWidth='content-max-width-md' display='flex' flexDirection='column' gap={4}>
      <Flex
        as='article'
        bgColor='orange.50'
        bgPosition='right'
        bgRepeat='no-repeat'
        bgSize='contain'
        border='1px solid'
        borderColor='orange.100'
        borderRadius='12px'
        overflow='hidden'
        position='relative'
        alignItems={{
          md: 'center',
        }}
        bgImage={{
          md: 'url(/images/experiments/ab_plp_redesign_2025/stripe-bg.svg)',
        }}
        flexDirection={{
          base: 'column-reverse',
          md: 'row',
        }}
        gap={{
          base: 6,
          md: '64px',
        }}
        padding={{
          base: 6,
          md: '56px',
        }}
      >
        <Box
          bgColor='white'
          border='1px solid'
          borderColor='orange.100'
          borderRadius='8px'
          flex={{
            md: '0 0 264px',
          }}
          padding={{
            base: 6,
            md: 8,
          }}
        >
          <Flex
            flexDirection='column'
            gap={4}
            paddingStart={4}
            position='relative'
            _before={{
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '2px',
              background: 'linear-gradient(180deg, #EE7622 0%, #FFF0E5 100%)',
            }}
          >
            <Heading as='p' color='orange.700' fontSize='3.375rem' fontWeight='500' lineHeight='2.75rem'>
              {outcomes.statisticValue}
              <Box as='span' fontSize='2rem' lineHeight='2.75rem'>
                {outcomes.statisticUnit}
              </Box>
            </Heading>
            <Text fontSize='1.125rem' lineHeight='1.625rem'>
              {t(outcomes.statisticDescriptionKey)} {supSlot}
            </Text>
          </Flex>
        </Box>

        <Flex flex={1} flexDirection='column' gap={4}>
          <Heading
            as='p'
            fontWeight='600'
            lineHeight='1.1'
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
          >
            {t(outcomes.headlineKey)}
          </Heading>
          <Markdown
            markdownText={t(outcomes.supportingTextKey)}
            components={{
              p: ({ children }) => <Text color='grey.500'>{children}</Text>,
              strong: ({ children }) => (
                <Text as='b' color='grey.900' fontWeight='600'>
                  {children}
                </Text>
              ),
            }}
          />
        </Flex>
      </Flex>
      {outcomes.statisticCitationKey && (
        <Text
          alignItems='baseline'
          alignSelf='center'
          color='grey.500'
          display='inline-flex'
          fontSize='0.875rem'
          gap={1}
          lineHeight='1.3125rem'
        >
          {supSlot} {t(outcomes.statisticCitationKey)}
        </Text>
      )}
    </ContentContainer>
  );
}

type MentorshipAsideProps = {
  titleKey: string;
  descriptionKey: string;
  companiesMentorsWorkAt: {
    logo: SanityImageType | null;
    name: string;
  }[];
};

function MentorshipAside({ titleKey, descriptionKey, companiesMentorsWorkAt }: MentorshipAsideProps) {
  const { t } = useTranslation();
  const objectPosition = useBreakpointValue({
    base: '-282px top',
    md: 'top',
  });
  const logoHeight = useBreakpointValue({
    base: '22px',
    md: '30px',
  });
  const bgImageHeight = '188px';
  return (
    <ContentContainer contentMaxWidth='content-max-width-md'>
      <Box
        aria-labelledby='mentors-title'
        as='aside'
        bgColor='cyan.100'
        borderRadius='12px'
        overflow='hidden'
        position='relative'
      >
        <Box
          height={bgImageHeight}
          position='absolute'
          top={0}
          width='1056px'
          left={{
            base: 0,
            md: '50%',
          }}
          transform={{
            md: 'translateX(-50%)',
          }}
        >
          <Image
            alt=''
            loading='lazy'
            sizes='1056px'
            src='/images/experiments/ab_plp_redesign_2025/mentors-bg.png'
            style={{ objectFit: 'cover', objectPosition, pointerEvents: 'none' }}
            fill
          />
        </Box>
        <Flex
          flexDirection='column'
          gap={{
            base: '32px',
            md: '64px',
          }}
          marginTop={{
            base: '110px',
            md: bgImageHeight,
          }}
          padding={{
            base: '0px 24px 32px',
            md: '0px 56px 56px',
          }}
        >
          <Box
            margin='0 auto'
            maxWidth='622px'
            width='100%'
            textAlign={{
              base: 'start',
              md: 'center',
            }}
          >
            <Heading
              as='h2'
              color='grey.900'
              fontWeight='600'
              id='mentors-title'
              lineHeight='1.1'
              mb={4}
              fontSize={{
                base: '1.75rem',
                md: '2.25rem',
              }}
              whiteSpace={{
                base: 'pre-wrap',
                md: 'unset',
              }}
            >
              {t(titleKey)}
            </Heading>
            <Text color='grey.500'>{t(descriptionKey)}</Text>
          </Box>
          <Flex
            alignItems='center'
            as='p'
            color='cyan.700'
            fontFamily='condensed'
            fontSize='0.9375rem'
            gap={4}
            lineHeight='1'
            textTransform='uppercase'
            _after={{
              content: '""',
              display: 'block',
              width: '100%',
              height: '0.5px',
              backgroundColor: 'cyan.700',
              flex: 1,
            }}
            _before={{
              content: '""',
              display: 'block',
              width: '100%',
              height: '0.5px',
              backgroundColor: 'cyan.700',
              flex: 1,
            }}
          >
            {t('home.outcomes.mentorship.ourMentorsWorkAt')}
          </Flex>
          <List
            alignItems='center'
            display='grid'
            gap={4}
            gridTemplateColumns='repeat(auto-fit, minmax(100px, 1fr))'
            justifyItems='center'
          >
            {companiesMentorsWorkAt.map((company) =>
              company.logo ? (
                <ListItem key={company.name}>
                  <SanityImage
                    alt={company.name}
                    image={company.logo}
                    style={{
                      height: logoHeight,
                    }}
                  />
                </ListItem>
              ) : null
            )}
          </List>
        </Flex>
      </Box>
    </ContentContainer>
  );
}

export async function transformStaticUdacityOutcomesSectionProps() {
  const { getCompanies } = await import('~/features/companies/controllers/get-companies');
  const { getCompany } = await import('~/features/companies/controllers/get-company');
  const { getEnterpriseVideoContent } = await import(
    '~/features/enterprise-resource/controllers/get-enterprise-video-content'
  );
  const company = await getCompany('Accenture');
  const videoContent = await getEnterpriseVideoContent('michael-miller-career-transformation');
  const targetCompanies = ['Amazon', 'Microsoft', 'Google', 'Facebook', 'Adobe', 'Qualcomm'];
  const companiesMentorsWorkAt = await getCompanies(targetCompanies).then((companies) =>
    companies.map((company) => ({
      logo: company.lofiLogoDark,
      name: company.name,
    }))
  );

  return {
    titleKey: 'home.outcomes.title',
    subtitleKey: 'home.outcomes.subtitle',
    descriptionKey: 'home.outcomes.description',
    featuredStory: {
      company: {
        logo: company?.lofiLogoLight ?? null,
        name: company?.name ?? null,
      },
      titleKey: 'home.outcomes.featuredStory.title',
      descriptionKey: 'home.outcomes.featuredStory.description',
      backgroundImageUrl: '/images/experiments/ab_plp_redesign_2025/michael-bg.png',
      mobileBackgroundImageUrl: '/images/experiments/ab_plp_redesign_2025/michael-bg-mobile.png',
      video: {
        _id: videoContent?._id ?? null,
        youTubeVideoId: videoContent?.videoData?.video?.youTubeVideoId ?? null,
        title: videoContent?.videoData?.video?.title ?? null,
      },
    },
    testimonials: [
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/tobias-frei.png',
        labelKey: 'testimonials.tobiasFrei.label',
        quoteKey: 'testimonials.tobiasFrei.quote',
        name: 'Tobias Frei',
        title: 'Digital Engineer',
        quoteColor: 'light',
        labelColor: 'dark',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/satyajit-ovelil.png',
        labelKey: 'testimonials.satyajitOvelil.label',
        quoteKey: 'testimonials.satyajitOvelil.quote',
        name: 'Satyajit Ovelil',
        title: 'Senior Analyst - Data & AI',
        quoteColor: 'light',
        labelColor: 'dark',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/janay-harris.png',
        labelKey: 'testimonials.janayHarris.label',
        quoteKey: 'testimonials.janayHarris.quote',
        name: 'Janay Harris',
        title: 'Tech Student',
        quoteColor: 'dark',
        labelColor: 'dark',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/nguyen-tuan-minh.png',
        labelKey: 'testimonials.nguyenTuanMinh.label',
        quoteKey: 'testimonials.nguyenTuanMinh.quote',
        name: 'Nguyễn Tuấn Minh',
        title: 'CHRO at FPT Software',
        quoteColor: 'light',
        labelColor: 'light',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/keith-myer.png',
        labelKey: 'testimonials.keithMyer.label',
        quoteKey: 'testimonials.keithMyer.quote',
        name: 'Keith Myer',
        title: 'Mobile Developer at Meta',
        quoteColor: 'dark',
        labelColor: 'light',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/eliza-fury.png',
        labelKey: 'testimonials.elizaFury.label',
        quoteKey: 'testimonials.elizaFury.quote',
        name: 'Eliza Fury',
        title: 'Software Engineer',
        quoteColor: 'light',
        labelColor: 'dark',
      },
      {
        imagePath: '/images/experiments/ab_plp_redesign_2025/federico-martini.png',
        labelKey: 'testimonials.federicoMartini.label',
        quoteKey: 'testimonials.federicoMartini.quote',
        name: 'Federico Martini',
        title: 'AI Engineering Manager & Tech Leader',
        quoteColor: 'light',
        labelColor: 'dark',
      },
    ],
    outcomes: {
      statisticValue: '90',
      statisticUnit: '%',
      statisticDescriptionKey: 'home.outcomes.statisticDescription',
      statisticCitationKey: 'home.outcomes.statisticCitation',
      headlineKey: 'home.outcomes.headline',
      supportingTextKey: 'home.outcomes.supportingText',
    },
    mentorship: {
      titleKey: 'home.outcomes.mentorship.title',
      descriptionKey: 'home.outcomes.mentorship.description',
      companiesMentorsWorkAt: [...companiesMentorsWorkAt].sort(
        (a, b) => targetCompanies.indexOf(a.name) - targetCompanies.indexOf(b.name)
      ),
    },
  };
}

export type StaticUdacityOutcomesSectionProps = Awaited<ReturnType<typeof transformStaticUdacityOutcomesSectionProps>>;
